// src/components/ao/ScoreCvModal.jsx
// Modal "Scorer un CV" : choisir une Ressource ou un profil du Pipe candidat déjà
// existant (recherche + CV déjà en base), ou uploader un nouveau fichier.
import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { resourcesAPI, pipelineAPI, aoAPI } from '../../services/api';
import { extractError } from '../../context/AuthContext';
import { Search, X, Upload, FileText, Briefcase, Users } from 'lucide-react';

export default function ScoreCvModal({ aoId, onClose, onScored, onUploadFile, uploading }) {
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [resources, setResources] = useState([]);
  const [pipelineEntries, setPipelineEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [scoringKey, setScoringKey] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query.trim()), 300);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      resourcesAPI.list({ search: debouncedQuery || undefined, limit: 15 }).catch(() => ({ data: [] })),
      pipelineAPI.list({ search: debouncedQuery || undefined, limit: 15 }).catch(() => ({ data: [] })),
    ]).then(([rRes, pRes]) => {
      if (cancelled) return;
      setResources(rRes.data || []);
      setPipelineEntries(pRes.data || []);
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [debouncedQuery]);

  const handlePick = async (sourceType, item) => {
    const key = `${sourceType}:${item.id}`;
    if (scoringKey) return;
    setScoringKey(key);
    const toastId = toast.loading('Scoring IA en cours… (15-30 sec)');
    try {
      const res = await aoAPI.scoreLinked(aoId, sourceType, item.id);
      toast.dismiss(toastId);
      toast.success(`${res.data.candidate_name || item.full_name} — score ajouté`);
      onClose();
      onScored && onScored();
    } catch (e) {
      toast.dismiss(toastId);
      toast.error(extractError(e));
      setScoringKey(null);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    onClose();
    onUploadFile(file);
  };

  const hasResults = resources.length > 0 || pipelineEntries.length > 0;

  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 560, width: '100%', maxHeight: '85vh', display: 'flex', flexDirection: 'column' }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700 }}>Scorer un CV</h3>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 2 }}>
              Choisissez une ressource, un profil du pipe, ou uploadez un fichier
            </div>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={16} /></button>
        </div>

        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 12, overflow: 'hidden', flex: 1 }}>
          <div style={{ position: 'relative' }}>
            <Search size={15} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
            <input
              autoFocus
              className="input"
              style={{ paddingLeft: 36 }}
              placeholder="Rechercher un nom, un poste…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>

          <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 14, minHeight: 200 }}>
            {loading ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-muted)', fontSize: '0.8125rem', padding: '20px 0', justifyContent: 'center' }}>
                <div className="spinner" style={{ width: 14, height: 14 }} /> Recherche…
              </div>
            ) : !hasResults ? (
              <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--text-muted)', fontSize: '0.8125rem' }}>
                Aucun résultat
              </div>
            ) : (
              <>
                {resources.length > 0 && (
                  <div>
                    <div className="ao-detail-label" style={{ color: 'var(--blue-600)', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                      <Briefcase size={12} /> Ressources
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      {resources.map((r) => (
                        <PickRow
                          key={r.id}
                          item={r}
                          hasCv={!!r.cv_available}
                          subtitle={[r.title, r.employee_type].filter(Boolean).join(' · ')}
                          scoring={scoringKey === `resource:${r.id}`}
                          disabled={!!scoringKey}
                          onClick={() => handlePick('resource', r)}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {pipelineEntries.length > 0 && (
                  <div>
                    <div className="ao-detail-label" style={{ color: '#059669', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                      <Users size={12} /> Pipe candidats
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      {pipelineEntries.map((p) => (
                        <PickRow
                          key={p.id}
                          item={p}
                          hasCv={!!(p.cv_link || p.profile_url)}
                          subtitle={[p.title, p.location].filter(Boolean).join(' · ')}
                          scoring={scoringKey === `pipeline:${p.id}`}
                          disabled={!!scoringKey}
                          onClick={() => handlePick('pipeline', p)}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        <div className="modal-footer" style={{ justifyContent: 'center' }}>
          <label
            className="btn btn-secondary btn-sm"
            style={{ cursor: uploading ? 'not-allowed' : 'pointer', opacity: uploading ? 0.7 : 1, width: '100%', justifyContent: 'center' }}
          >
            {uploading ? (
              <><div className="spinner" style={{ width: 13, height: 13 }} /> Scoring…</>
            ) : (
              <><Upload size={13} /> Ou uploader un nouveau fichier CV</>
            )}
            <input
              type="file"
              accept=".pdf,.doc,.docx,.txt"
              onChange={handleFileChange}
              style={{ display: 'none' }}
              disabled={uploading}
            />
          </label>
        </div>
      </div>
    </div>
  );
}

function PickRow({ item, hasCv, subtitle, scoring, disabled, onClick }) {
  return (
    <button
      onClick={hasCv ? onClick : undefined}
      disabled={!hasCv || disabled}
      title={hasCv ? undefined : 'Aucun CV disponible pour ce profil'}
      style={{
        display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px',
        border: '1px solid var(--border)', borderRadius: 8, background: 'var(--bg-elevated)',
        cursor: hasCv && !disabled ? 'pointer' : 'not-allowed',
        opacity: hasCv ? 1 : 0.45,
        textAlign: 'left', width: '100%',
      }}
    >
      <div className="avatar avatar-sm" style={{ flexShrink: 0 }}>{(item.full_name || '?')[0]}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: '0.8125rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {item.full_name || 'Sans nom'}
        </div>
        {subtitle && (
          <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {subtitle}
          </div>
        )}
      </div>
      {scoring ? (
        <div className="spinner" style={{ width: 13, height: 13, flexShrink: 0 }} />
      ) : hasCv ? (
        <FileText size={14} color="#16A34A" style={{ flexShrink: 0 }} />
      ) : (
        <FileText size={14} color="var(--text-muted)" style={{ flexShrink: 0 }} />
      )}
    </button>
  );
}
